import { useMutation } from "@outschool/ui-apollo";
import { cloneDeep } from "lodash";
import React from "react";

import { ClassroomQuery, SinglePostQuery } from "../../generated/graphql";
import { validOptimisticResponse } from "../ApolloClient";
import { deleteClassPostCommentMutation } from "../queries/ClassPostMutations";

export interface DeleteCommentMutationProps {
  commentUid: string;
  postUid: string;
  threadParentCommentUid?: string;
}

export const isSinglePostQuery = (q: any): q is SinglePostQuery =>
  !!q?.classroom?.postByUid?.uid;

export const isClassroomQuery = (q: any): q is ClassroomQuery =>
  !!q?.classroom?.posts || !!q?.classroom?.unpublishedPosts;

export function useDeleteClassPostCommentMutation({
  updateQuery,
  updateQueryVariables
}: $TSFixMe) {
  const [mutate] = useMutation(deleteClassPostCommentMutation);

  return React.useMemo(
    () =>
      ({ commentUid, postUid }: DeleteCommentMutationProps) => {
        return mutate({
          variables: {
            classPostCommentUid: commentUid
          },
          optimisticResponse: validOptimisticResponse({
            __typename: "Mutation",
            deleteClassPostComment: true
          }),
          update(store) {
            const oldData = store.readQuery<ClassroomQuery | SinglePostQuery>({
              query: updateQuery,
              variables: updateQueryVariables
            });
            const data = cloneDeep(oldData);
            if (isSinglePostQuery(data)) {
              // Single post page
              const classPost = data.classroom.postByUid;
              if (classPost) {
                classPost.comments = classPost.comments.filter(
                  c => c.uid !== commentUid
                );
              }
            } else {
              // Classroom page
              const classPost = data?.classroom.posts.find(
                p => p.uid === postUid
              );
              if (classPost && classPost.commentsPreview.comments) {
                classPost.commentsPreview.comments =
                  classPost.commentsPreview.comments.filter(
                    c => c.uid !== commentUid
                  );
              }
            }
            store.writeQuery({
              query: updateQuery,
              variables: updateQueryVariables,
              data
            });
          }
        });
      },
    [mutate, updateQueryVariables, updateQuery]
  );
}
